import axios, { AxiosRequestConfig, AxiosRequestHeaders } from 'axios';
import { BASE_URL } from 'config';

const TOKEN_STORAGE_KEY = 'serviceToken';
const isBrowser = typeof window !== 'undefined';

export const getServiceToken = () => {
  if (!isBrowser) {
    return null;
  }
  return window.localStorage.getItem(TOKEN_STORAGE_KEY) ?? window.sessionStorage.getItem(TOKEN_STORAGE_KEY);
};

const axiosServices = axios.create({ baseURL: BASE_URL || 'http://localhost:3010/' });

type UnauthorizedHandler = (() => void) | null;
let unauthorizedHandler: UnauthorizedHandler = null;

export const setUnauthorizedHandler = (handler: UnauthorizedHandler) => {
  unauthorizedHandler = handler;
};

const redirectToLogin = () => {
  if (typeof unauthorizedHandler === 'function') {
    unauthorizedHandler();
    return;
  }

  if (isBrowser && !window.location.pathname.includes('/login')) {
    window.location.replace('/login');
  }
};

// ==============================|| AXIOS - FOR AUTH SERVICES ||============================== //

axiosServices.interceptors.request.use(
  async (config) => {
    const accessToken = getServiceToken();
    if (accessToken) {
      const headers = (config.headers ?? {}) as AxiosRequestHeaders;
      headers['Authorization'] = `Bearer ${accessToken}`;
      config.headers = headers;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosServices.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if ((status === 401 || status === 403) && !window.location.pathname.includes('/login')) {
      redirectToLogin();
    }
    return Promise.reject((error.response && error.response.data) || error);
  }
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if ((status === 401 || status === 403) && !window.location.pathname.includes('/login')) {
      redirectToLogin();
    }
    return Promise.reject(error);
  }
);

export default axiosServices;

const normalizeFetcherArgs = (args: string | [string, AxiosRequestConfig]): [string, AxiosRequestConfig] => {
  if (Array.isArray(args)) {
    const [url, config] = args;
    return [url, config ?? {}];
  }

  const url = args.startsWith('/') ? args : `/${args}`;
  return [url, {}];
};

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = normalizeFetcherArgs(args);
  const res = await axiosServices.get(url, { ...config });
  return res.data;
};

export const fetcherPost = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = normalizeFetcherArgs(args);
  const res = await axiosServices.post(url, { ...config });
  return res.data;
};
